import { Injectable } from '@angular/core';
import { NIBRAS_FILTER, NIBRAS_HOOK } from './model/hooks/hook.constants';
import { GenericObjectValue } from './model/request/request.model';

@Injectable()
export class NibrasHooksService {
  private hooks: Map<string, NibrasHookModel> = new Map();
  private filters: Map<string, NibrasHookModel> = new Map();

  constructor() {
    this.registerHooks(Object.values(NIBRAS_HOOK));
    this.registerFilter(Object.values(NIBRAS_FILTER));
  }

  registerHooks(hooks: string[]) {
    if (!hooks || !hooks.length) return;
    hooks.forEach(h => this.createAction(h));
  }

  registerFilter(filters: string[]) {
    if (!filters || !filters.length) return;
    filters.forEach(h => this.createFilter(h));
  }

  createAction(hookName: string) {
    this.hooks.set(hookName, new NibrasHookModel());
  }

  addAction(
    hookName: string,
    callback: Function,
    thisArg: Object,
    order: number = 1,
    triggerOnlyOnce = false
  ) {
    if (!this.hooks.has(hookName)) {
      this.createAction(hookName);
    }

    this.hooks.get(hookName).add(callback, order, thisArg, triggerOnlyOnce);
  }

  removeAction(hookName: string) {
    if (this.hooks.has(hookName)) {
      this.hooks.delete(hookName);
    }
  }

  doAction(hookName: string, context: GenericObjectValue = null, ...args: object[]) {
    this.doActionWithCondition(hookName, context, null, args);
  }

  doActionWithCondition(
    hookName: string,
    context: GenericObjectValue = null,
    condition: () => boolean,
    ...args: object[]
  ) {
    const hook = this.hooks.get(hookName);
    if (!hook) {
      return;
    }

    hook.executeMethodsWithCondition(context, condition, args);
  }

  createFilter(filterName: string) {
    this.filters.set(filterName, new NibrasHookModel());
  }

  addFilter(filterName: string, callback: Function, thisArg: Object, order: number = 1) {
    if (!this.filters.has(filterName)) {
      this.createAction(filterName);
    }

    this.filters.get(filterName).add(callback, order, thisArg);
  }

  removeFilter(filterName: string) {
    if (this.filters.has(filterName)) {
      this.filters.delete(filterName);
    }
  }

  //TODO: make these method typesafe.
  applyFilter(filterName: string, context: GenericObjectValue = null, ...args: object[]) {
    if (!this.filters.get(filterName)) {
      return context;
    }

    return this.filters.get(filterName).chainMethods(context, args);
  }
}

export class NibrasHookModel {
  methods: NibrasHooksFunctions[] = [];
  context: GenericObjectValue = null;

  add(callback: Function, order: number = 1, thisRef: Object = null, triggerOnlyOnce = false) {
    this.methods.push({ callback, order, thisRef, triggerOnlyOnce });
  }

  executeMethods(context: GenericObjectValue, args: object[]) {
    this.executeMethodsWithCondition(context, null, args);
  }

  executeMethodsWithCondition(
    context: GenericObjectValue,
    condition: () => boolean,
    args: object[]
  ) {
    this.context = context;
    if (!this.methods || !this.methods.length) {
      return;
    }

    this.methods.forEach((cb, index) => {
      if (condition && !condition.call(cb.thisRef)) {
        return;
      }

      cb.callback.call(cb.thisRef, this.context, args);

      if (cb.triggerOnlyOnce) {
        this.methods.splice(index, 1);
      }
    });
  }

  chainMethods(context: GenericObjectValue, args: object[]) {
    this.context = context;
    if (!this.methods || !this.methods.length) {
      return this.context;
    }

    this.methods.forEach(cb => (this.context = cb.callback.call(cb.thisRef, this.context, args)));
    return this.context;
  }
}

export interface NibrasHooksFunctions {
  callback: Function;
  order: number;
  thisRef: Object;
  triggerOnlyOnce?: boolean;
}
